<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none white pb-0">
			<ul class="settings-tabs">
				<li
					class="settings-tabs-tab"
					:class="$route.path.includes('/users') ? 'active' : null"
					@click="$router.push('/settings/security/users')"
				>
					Users
				</li>
				<li
					class="settings-tabs-tab"
					:class="$route.path.includes('/roles') ? 'active' : null"
					@click="
						$router.push('/settings/security/roles');
						roleTab = filteredRoles[0]?.roleId;
					"
				>
					Roles
				</li>
			</ul>
		</v-col>

		<v-col v-if="$route.path.includes('/users')" cols="12" class="flex-auto">
			<div class="settings-roles">
				<div class="settings-roles-header">
					<div class="settings-roles-header-actions">
						<v-btn class="new-user-btn" text color="accent" @click="addUser">
							<v-icon>mdi-plus</v-icon>
							New User
						</v-btn>
					</div>
				</div>

				<div class="table settings-table">
					<div v-if="initData" class="loading-overlay">
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
						<div class="loading-dot">&nbsp;</div>
					</div>
					<div class="table-container">
						<table>
							<thead>
								<tr style="border-left: 1px solid #d5d6db; border-right: 1px solid #d5d6db">
									<th style="width: 1%">&nbsp;</th>
									<th style="width: 20%">Name</th>
									<th style="width: 18%">Role</th>
									<th style="width: 10%">Rep #</th>
									<th style="width: 11%">Phone Number</th>
									<th style="width: 19%">Email</th>
									<th style="width: 14%">Inactive Date</th>
									<th style="width: 7%">&nbsp;</th>
								</tr>
							</thead>
							<tbody>
								<tr
									style="border-left: 1px solid #d5d6db; border-right: 1px solid #d5d6db"
									v-for="user in users"
									:key="user.userId"
								>
									<td v-if="!user.isActive || user.inactiveDate">
										<v-icon v-if="!user.isActive" color="primary" style="font-size: 2rem"
											>mdi-account-cancel</v-icon
										>
										<v-icon v-else color="primary" style="font-size: 2rem"
											>mdi-timer-cancel-outline</v-icon
										>
									</td>
									<td v-else></td>
									<td>{{ `${user.firstName} ${user.lastName}` }}</td>
									<td>{{ user.roles?.map((role) => role.normalizedName).join(", ") }}</td>
									<td>{{ user.repNumber }}</td>
									<td>{{ user.phoneNumber }}</td>
									<td>{{ user.email }}</td>
									<td>{{ formatDate(user.inactiveDate) }}</td>
									<td class="text-right">
										<v-btn
											icon
											color="primary"
											@click="
												activeUser = user;
												editUser();
											"
											><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
										</v-btn>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div class="table-controls">
					<v-btn depressed small :disabled="!hasPreviousPage" @click="pageIndexOffset -= 15">
						Previous
					</v-btn>
					<v-btn depressed small :disabled="!hasNextPage" @click="pageIndexOffset += 15">
						Next Page
					</v-btn>
				</div>
			</div>
		</v-col>

		<v-col v-else cols="12" class="flex-auto">
			<div class="settings-roles">
				<div class="settings-roles-header">
					<ul class="settings-tabs">
						<li
							v-for="role in filteredRoles"
							:key="role?.roleId"
							class="settings-tabs-tab"
							:class="roleTab == role?.roleId ? 'active' : null"
							@click="roleTab = role?.roleId"
						>
							{{ role.normalizedName }}
						</li>
					</ul>

					<div class="settings-roles-header-actions">
						<v-btn class="settings-roles-header-actions-btn" text color="accent" @click="editRole">
							<v-icon small class="mr-1">mdi-pencil</v-icon>
							Edit Role
						</v-btn>
						<v-btn class="settings-roles-header-actions-btn" text color="accent" @click="addRole">
							<v-icon>mdi-plus</v-icon>
							New Role
						</v-btn>
					</div>
				</div>

				<!-- role permissions table -->
				<div class="table settings-roles-table">
					<div class="table-container">
						<table>
							<thead>
								<tr>
									<th style="width: 5%">&nbsp;</th>
									<th style="width: 20%">Module</th>
									<th style="width: 25%">Resource</th>
									<th style="width: 50%">Description</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(perm, i) in allRolePermissions
										.find((rp) => rp?.roleId === roleTab)
										?.permissions?.filter((p) => p.isActive) || []"
									:key="i"
								>
									<td>
										<v-icon dense color="gray">{{ getModuleIcon(perm.module) }}</v-icon>
									</td>
									<td>{{ getModuleName(perm.module) }}</td>
									<td>
										<strong>{{ perm.resource }}</strong>
										<span class="text--secondary ml-1">({{ perm.action }})</span>
									</td>
									<td class="text--secondary">{{ perm.description }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</v-col>

		<!-- user detail modal -->
		<v-overlay v-if="showUserModal" :dark="false" :absolute="true">
			<div class="input-modal settings-user-modal">
				<div class="input-modal-header">
					<h1 class="input-modal-header-title">{{ isAddNewUser ? "New" : "Edit" }} User</h1>
					<v-btn icon @click="showUserModal = false">
						<v-icon v-text="'mdi-close'"></v-icon>
					</v-btn>
				</div>
				<v-form ref="userForm">
					<div class="input-modal-content">
						<v-alert v-if="userErrorMessage" type="error" dense dismissible @input="userErrorMessage = null" class="mb-3">
							{{ userErrorMessage }}
						</v-alert>
						<div class="settings-user-modal-form">
							<div class="form-input">
								<label>First Name:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.firstName"
									required
									:rules="[(v) => !!v || 'First name is required']"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Last Name:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.lastName"
									required
									:rules="[(v) => !!v || 'Last name is required']"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Phone Number:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.phoneNumber"
								></v-text-field>
							</div>

							<div class="form-input">
								<label>Email:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.email"
									required
									:rules="[(v) => !!v || 'Email is required']"
								></v-text-field>
							</div>

							<div class="form-input">
								<label>Roles:</label>
								<v-combobox
									hide-details="auto"
									dense
									multiple
									outlined
									v-model="activeUser.roles"
									:items="userAssignableRoles"
									item-text="normalizedName"
									item-value="roleId"
								></v-combobox>
							</div>

							<div v-if="isRepSelected" class="form-input">
								<label>Rep #:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.repNumber"
								></v-text-field>
							</div>

							<div v-if="!isAddNewUser" class="form-input">
								<label>Inactive Date</label>
								<v-menu
									v-model="showInactiveDate"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="290px"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											append-icon="mdi-calendar"
											@click:append="showInactiveDate = true"
											outlined
											dense
											v-bind="attrs"
											v-on="on"
											@blur="
												activeUser.inactiveDate = parseDate(formatDate(activeUser.inactiveDate))
											"
											hide-details="auto"
											:value="formatDate(activeUser.inactiveDate)"
											clearable
											@click:clear="activeUser.inactiveDate = null"
										></v-text-field>
									</template>
									<v-date-picker
										color="accent"
										v-model="activeUser.inactiveDate"
										@input="showInactiveDate = false"
									></v-date-picker>
								</v-menu>
							</div>

							<div class="settings-user-modal-form-divider">&nbsp;</div>

							<div class="form-input">
								<label>{{ isAddNewUser ? "Password" : "New Password" }}:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.newPassword"
									:rules="isAddNewUser ? passwordRules : []"
									:required="isAddNewUser"
									:type="showPassword ? 'text' : 'password'"
									:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
									@click:append="showPassword = !showPassword"
								></v-text-field>
							</div>
							<div class="form-input">
								<label>Confirm Password:</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="activeUser.confirmPassword"
									:rules="isAddNewUser ? confirmPasswordRules : []"
									:required="isAddNewUser"
									:type="showPassword ? 'text' : 'password'"
									:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
									@click:append="showPassword = !showPassword"
								></v-text-field>
							</div>
						</div>

						<div class="input-modal-content-actions">
							<v-btn outlined depressed @click="showUserModal = false">Cancel</v-btn>
							<v-btn color="primary" depressed @click="saveUser">Save</v-btn>
						</div>
					</div>
				</v-form>
			</div>
		</v-overlay>

		<!-- new role group modal -->
		<v-overlay v-if="showRoleModal" :dark="false" :absolute="true">
			<div class="input-modal settings-user-modal">
				<div class="input-modal-header">
					<h1 class="input-modal-header-title">Role Details</h1>
					<v-btn icon @click="showRoleModal = false">
						<v-icon v-text="'mdi-close'"></v-icon>
					</v-btn>
				</div>
				<div class="input-modal-content">
					<div class="settings-user-modal-form">
						<div class="form-input">
							<label>Role Name:</label>
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="activeRoleGroup.name"
								:disabled="
									(activeRole.roleId === 4 || activeRole.roleId === 5) && activeRoleGroup.roleId > 0
								"
							></v-text-field>
						</div>
					</div>

					<div class="settings-user-modal-roles">
						<h4 class="mt-2">Assign Role Permissions</h4>
						<div class="settings-user-modal-roles-list">
							<v-text-field
								v-model="searchPermissionTerm"
								outlined
								dense
								hide-details="auto"
								label="Search Permissions"
								class="mb-1"
								clearable
							></v-text-field>
							<v-list outlined maxHeight="500" style="overflow: auto">
								<v-list-item-group v-model="selectedPermissions" multiple>
									<template v-for="(item, i) in filteredDistinctPermissions">
										<v-list-item :key="i" :value="item.claimValue">
											<template v-slot:default="{ active }">
												<v-list-item-action>
													<v-checkbox
														:input-value="active"
														color="primary"
														on-icon="$checkboxOn"
														off-icon="$checkboxOff"
													></v-checkbox>
												</v-list-item-action>
												<v-list-item-content>
													<v-list-item-title>
														<v-icon small class="mr-1">{{ getModuleIcon(item.module) }}</v-icon>
														<strong>{{ item.resource }}</strong>
														<span class="text--secondary ml-1">({{ item.action }})</span>
													</v-list-item-title>
													<v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
												</v-list-item-content>
											</template>
										</v-list-item>
									</template>
								</v-list-item-group>
							</v-list>
						</div>
					</div>

					<div class="input-modal-content-actions">
						<v-btn
							v-if="!isAddNewRole && activeRole?.roleId && activeRole?.roleId > 5"
							color="red"
							outlined
							depressed
							@click="deleteWarning = true"
							>Delete</v-btn
						>
						<v-btn
							outlined
							depressed
							@click="
								showRoleModal = false;
								activeRoleGroup.name = '';
								activeRoleGroup.permissions = [];
							"
							>Cancel</v-btn
						>
						<v-btn
							color="primary"
							depressed
							:disabled="!activeRoleGroup.name || activeRoleGroup.name.length === 0"
							@click="saveRole"
							>Save</v-btn
						>
					</div>
				</div>
			</div>
		</v-overlay>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete this role?'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="deleteRole"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import {
	permissionMetadata,
	permissionModules,
	getPermissionMetadata,
	formatPermissionDisplay,
	getPermissionsGroupedByModule,
} from "@/config/permissionMetadata";

export default {
	name: "Security",
	components: {
		WarningModal,
	},
	data() {
		return {
			initData: false,
			users: [],
			activeUser: null,
			roleTab: 1,
			specialRoles: [],
			roleEditMode: false,
			showUserModal: false,
			activeRoleGroup: {
				name: "",
				permissions: [],
			},
			showRoleModal: false,
			showExpiredDate: false,
			formattedExpiredDate: null,
			pageIndexOffset: 0,
			hasNextPage: false,
			hasPreviousPage: false,
			isAddNewRole: false,
			isAddNewUser: false,
			deleteWarning: false,
			passwordRules: [
				(v) => !!v || "Password is required",
				(v) => (v && v.length >= 6) || "Password must be at least 6 characters long",
				(v) => /[A-Z]/.test(v) || "Password must contain at least one uppercase letter",
				(v) => /[a-z]/.test(v) || "Password must contain at least one lowercase letter",
				(v) => /[0-9]/.test(v) || "Password must contain at least one digit",
			],
			confirmPasswordRules: [
				(v) => !!v || "Confirming password is required",
				(v) => v === this.activeUser.newPassword || "Passwords must match",
			],
			showInactiveDate: false,
			searchPermissionTerm: "",
			selectedPermissions: [],
			userErrorMessage: null,
			showPassword: false,
		};
	},
	apollo: {
		permissions: {
			query() {
				return gql`
					query {
						permissions {
							claimValue
						}
					}
				`;
			},
			update: (data) => data.permissions,
		},
		roles: {
			query() {
				return gql`
					query {
						roles {
							roleId
							name
							normalizedName
							permissions {
								name
							}
						}
					}
				`;
			},
			update: (data) => data.roles,
		},
	},
	methods: {
		// Permission metadata helpers
		getModuleIcon(moduleName) {
			return permissionModules[moduleName]?.icon || "mdi-shield-account-outline";
		},
		getModuleName(moduleName) {
			return permissionModules[moduleName]?.name || moduleName;
		},
		extractModule(claimValue) {
			const splitIndex = claimValue.indexOf("_");
			return splitIndex > 0 ? claimValue.substring(0, splitIndex) : claimValue;
		},

		formatDate(date) {
			if (!date) return "";

			// Ensure the date is a string
			const dateString = typeof date === "string" ? date : date.toISOString();
			const [year, month, day] = dateString.substring(0, 10).split("-");
			return `${month}/${day}/${year}`;
		},

		parseDate(date) {
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		renderPasswordExpiration(expirationDate) {
			const currentDate = new Date();

			return Math.floor((new Date(expirationDate) - currentDate) / (1000 * 60 * 60 * 24));
		},

		addUser() {
			this.activeUser = {
				firstName: "",
				lastName: "",
				email: "",
				roles: [],
				phone: "",
				inactiveDate: null,
			};
			this.isAddNewUser = true;
			this.userErrorMessage = null;
			this.showPassword = false;
			this.showUserModal = true;
		},

		editUser() {
			this.isAddNewUser = false;
			this.userErrorMessage = null;
			this.showPassword = false;
			this.showUserModal = true;
		},

		addRole() {
			this.isAddNewRole = true;
			this.activeRoleGroup.roleId = null;
			this.activeRoleGroup.name = "";
			this.activeRoleGroup.permissions = [];
			this.showRoleModal = true;
		},

		editRole() {
			this.isAddNewRole = false;
			this.activeRoleGroup.roleId = this.activeRole?.roleId;
			this.activeRoleGroup.name = this.activeRole.name;
			this.activeRoleGroup.permissions = this.activeRole.permissions
				? this.activeRole.permissions.filter((p) => p.isActive).map((p) => p.claimValue)
				: [];
			this.showRoleModal = true;
		},

		async deleteRole() {
			try {
				this.deleteWarning = false;
				const roleId = this.activeRoleGroup?.roleId;
				const res = await this.$http.delete(`/api/settings/role/${roleId}`);
				console.log(res);
				this.showRoleModal = false;
				this.init();
				this.roleTab = this.filteredRoles[0]?.roleId;
			} catch (error) {
				console.log(error);
			}
		},

		async saveRole() {
			try {
				this.showRoleModal = false;
				const roleToSave = { ...this.activeRoleGroup, permissions: this.selectedPermissions };
				const res = this.activeRoleGroup.roleId
					? await this.$http.put(`/api/settings/role`, roleToSave)
					: await this.$http.post(`/api/settings/role`, roleToSave);
				console.log(res);
				this.init();
			} catch (error) {
				console.log(error);
			}
		},

		async saveUser() {
			try {
				if (this.$refs.userForm.validate()) {
					if (this.isAddNewUser) {
						await this.registerNewUser();
					} else {
						await this.updateUser();
					}
				}
			} catch (error) {
				console.log(error);
			}
		},

		async registerNewUser() {
			const userData = {
				Username: this.activeUser.email,
				Password: this.activeUser.newPassword,
				FirstName: this.activeUser.firstName,
				LastName: this.activeUser.lastName,
				Email: this.activeUser.email,
				PhoneNumber: this.activeUser.phoneNumber,
				Roles: this.activeUser.roles.map((role) => role.name),
				RepNumber: this.activeUser.repNumber,
			};

			try {
				const response = await this.$http.post("/api/users", userData);
				if (response.status === 200) {
					this.showUserModal = false;
					this.userErrorMessage = null;
					this.init();
				} else {
					this.userErrorMessage = response.data?.message || "Failed to register user";
					console.error("Failed to register user:", response.data);
				}
			} catch (error) {
				this.userErrorMessage = error.response?.data?.message || "Error registering user";
				console.error("Error during user registration:", error);
			}
		},

		async updateUser() {
			const userData = {
				UserId: this.activeUser.userId,
				Username: this.activeUser.username,
				FirstName: this.activeUser.firstName,
				LastName: this.activeUser.lastName,
				Email: this.activeUser.email,
				PhoneNumber: this.activeUser.phoneNumber,
				Roles: this.activeUser.roles.map((role) => role.name),
				Password: this.activeUser.newPassword,
				InactiveDate: this.activeUser.inactiveDate,
				RepNumber: this.activeUser.repNumber,
			};

			try {
				const response = await this.$http.put("/api/users", userData);
				if (response.status === 200) {
					this.showUserModal = false;
					this.userErrorMessage = null;
					this.init();
				} else {
					this.userErrorMessage = response.data?.message || "Failed to update user";
					console.error("Failed to update user:", response.data);
				}
			} catch (error) {
				this.userErrorMessage = error.response?.data?.message || "Error updating user";
				console.error("Error during user update:", error);
			}
		},

		async getUsers() {
			try {
				const res = await this.$http.get(`/api/users`);
				this.users = res.data.users;

				if (this.initData) this.initData = false;
			} catch (error) {
				console.log(error);
			}
		},

		loadRolePermissions(roleId) {
			console.log("loadRolePermissions", roleId);
			const role = this.roles.find((r) => r.roleId === roleId);
			if (role && role.permissions && this.activeRoleGroup.roleId) {
				this.selectedPermissions = role.permissions.map((p) => p.name);
			} else {
				this.selectedPermissions = [];
			}
		},

		async init() {
			this.initData = true;
			await this.$apollo.queries.roles.refetch();
			await this.getUsers();
		},
	},
	computed: {
		allRolePermissions() {
			if (!this.roles || !this.permissions) {
				return [];
			}

			return this.roles.map((role) => {
				// For each role, map over this.permissions
				const permissions = this.permissions.map((permission) => {
					// Determine if the permission is active for the current role
					const isActive = !!role.permissions.find((p) => p.name === permission.claimValue);

					// Get metadata for this permission
					const metadata = getPermissionMetadata(permission.claimValue);
					const formatted = formatPermissionDisplay(permission.claimValue);

					return {
						...permission,
						isActive,
						module: metadata?.module || this.extractModule(permission.claimValue),
						resource: metadata?.resource || formatted.resource,
						action: metadata?.action || formatted.action,
						description: metadata?.description || "",
					};
				});

				return {
					...role,
					permissions,
				};
			});
		},
		distinctPermissions() {
			if (!this.permissions) return [];

			return this.permissions.map((permission) => {
				// Get metadata for this permission
				const metadata = getPermissionMetadata(permission.claimValue);
				const formatted = formatPermissionDisplay(permission.claimValue);

				return {
					...permission,
					module: metadata?.module || this.extractModule(permission.claimValue),
					resource: metadata?.resource || formatted.resource,
					action: metadata?.action || formatted.action,
					description: metadata?.description || "",
				};
			});
		},
		filteredDistinctPermissions() {
			if (!this.searchPermissionTerm) {
				return this.distinctPermissions;
			}
			const searchTerm = this.searchPermissionTerm.toLowerCase();
			return this.distinctPermissions.filter(
				(permission) =>
					permission.claimValue.toLowerCase().includes(searchTerm) ||
					permission.module?.toLowerCase().includes(searchTerm) ||
					permission.resource?.toLowerCase().includes(searchTerm) ||
					permission.action?.toLowerCase().includes(searchTerm) ||
					permission.description?.toLowerCase().includes(searchTerm)
			);
		},
		activeRole() {
			return this.allRolePermissions.find((rp) => rp?.roleId === this.roleTab);
		},
		filteredRoles() {
			if (!this.roles) return [];
			return this.roles.filter((r) => r.roleId !== 1 && r.roleId !== 2 && r.roleId !== 3);
		},
		userAssignableRoles() {
			if (!this.roles) return [];
			// Filter out SUPER_ADMIN (roleId 3) from user role assignment
			return this.roles.filter((r) => r.normalizedName !== "SUPER_ADMIN");
		},
		isRepSelected() {
			return this.activeUser?.roles?.some((role) => role.normalizedName === "REP") ?? false;
		},
	},
	watch: {
		roleTab(newVal) {
			this.loadRolePermissions(newVal);
		},
		searchPermissionTerm() {
			this.$nextTick(() => {
				if (this.activeRoleGroup.roleId > 0) {
					this.loadRolePermissions(this.roleTab);
				}
			});
		},
		showRoleModal(newVal) {
			if (newVal) {
				this.searchPermissionTerm = "";

				if (this.activeRoleGroup.roleId) {
					this.loadRolePermissions(this.roleTab);
				} else {
					// new role
					this.selectedPermissions = [];
				}
			}
		},
	},
	async mounted() {
		await this.init();
	},
};
</script>

<style lang="scss" scoped>
.full-height {
	margin-top: -12px !important;
}

.settings {
	&-tabs {
		list-style: none;
		display: inline-flex;
		flex-flow: row nowrap;
		padding-left: 0.8rem;
		background-color: $color-white;

		&-tab {
			padding: 0.5rem 1rem;
			padding-bottom: 1rem;
			color: $color-gray;
			border-bottom: 2px solid transparent;
			cursor: pointer;
			transition: all 0.3s;

			&:hover {
				border-bottom: 2px solid $color-gray;
			}

			&.active {
				color: $color-black;
				border-bottom: 2px solid $color-tertiary;
			}
		}
	}

	&-header {
		&-actions {
			grid-column: 3;
			margin-left: auto;

			.v-btn {
				min-width: 13rem;
				text-transform: initial;
				background-color: $color-white;
				border-color: $color-gray;
				margin-right: 0.8rem;

				&:last-child {
					margin-right: 0;
				}
			}
		}
	}

	&-roles {
		display: flex;
		flex-flow: column;
		height: 100%;
		background-color: $color-white;
		border-radius: 3px;
		padding: 1.2rem 2rem;

		&-header {
			display: flex;
			flex-flow: row wrap;
			width: 100%;
			border-bottom: 1px solid $color-gray;
			margin-bottom: 2rem;

			&-actions {
				margin-left: auto;
			}

			.settings-tabs {
				padding-left: 0;
			}
		}

		&-table {
			border: 1px solid $color-gray-2;
		}
	}

	&-user-modal {
		&-header {
			position: relative;
			display: flex;
			flex-flow: row wrap;
			width: 100%;

			&-tabs {
				position: relative;
				padding-left: 0;
				margin-bottom: 0.5rem;
				width: 100%;

				.active {
					z-index: 1;
				}

				&::after {
					content: "";
					display: block;
					position: absolute;
					bottom: 0;
					width: 100%;
					border-bottom: 1px solid $color-gray;
					z-index: 0;
				}
			}

			&-actions {
				position: absolute;
				right: 0;
			}
		}

		&-form {
			display: flex;
			flex-flow: column wrap;

			.form-input {
				margin: 0.75rem 0;
			}

			&-date {
				width: 15rem;
				flex: initial;
			}

			&-divider {
				display: block;
				height: 1px;
				width: 100%;
				margin-top: 0.75rem;
				background-color: $color-gray;
			}
		}

		&-roles {
			position: relative;

			&-list {
				margin-top: 1.5rem;
				display: flex;
				flex-flow: column;

				label {
					background-color: $color-gray-light-2;
					padding: 0.5rem 1rem;
					margin: 0;
				}

				.v-list {
					min-height: 25rem;
					padding-top: 0;
					padding-bottom: 0;

					::v-deep {
						.v-list-item {
							min-height: initial;
						}
						.v-list-item__action {
							margin: 0;
							margin-right: 1rem;
						}
						.v-list-item__content {
							padding: 0.8rem 0;
						}
						.v-input--selection-controls__ripple {
							display: none;
						}
					}
				}
			}
		}
	}
}
</style>

<style lang="scss">
.new-user-container {
	padding-bottom: 1rem;
	display: flex;
	justify-content: flex-end;
}
</style>
